<template>
  <ui-collapse
    ref="myElement"
    :class="{ disabled: !hierarchyTree }"
    class="hierarchy"
    data-test="filter-and-sort-location-hierarchy"
    data-filter-location-scroll-target
    :collapse-id="`collapse-filter-and-sort-location-hierarchy`"
    @collapse-open="scrollToHierarchy"
  >
    <template #ui-collapse-title>
      <translate>Location:</translate>
      <strong>{{ hierarchyName }}</strong>
    </template>
    <template #ui-collapse-subtitle>
      <span v-if="showCount" class="count">{{
        facetCounts[hierarchyPath]
      }}</span>
    </template>
    <template #ui-collapse-body>
      <base-tree v-if="hierarchyTree" v-slot="{ node }" :root="hierarchyTree">
        <base-radio
          v-if="shouldShowLocation(node.path)"
          :model-value="node.path"
          :checked="node.path === hierarchyPath"
          :class="{ selected: node.path === hierarchyPath }"
          name="location"
          @update:model-value="selectHierarchy(node)"
        >
          <translate>{{ node.label }}</translate>
          <span v-if="showCounts(node.path)" class="count">{{
            facetCounts[node.path]
          }}</span>
        </base-radio>
      </base-tree>
    </template>
  </ui-collapse>
</template>

<script lang="ts">
import { FacetCount } from '~/apps/facets/domain/FacetCounts'
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'
import { logger } from '../../../../utility/logger'
import { HierarchyTree } from '../../../hierarchies/domain/types'
import { scrollIntoView } from '~/utility/scroll'

@Component({
  async setup() {
    const searchWizardStore = useSearchWizardStore(usePinia())
    await searchWizardStore.waitForDataLoad()
    return {
      searchWizardStore,
    }
  },
  emits: ['changed', 'hierarchy'],
})
class FilterAndSortLocation extends Vue {
  searchWizardStore: ReturnType<typeof useSearchWizardStore>

  @Prop()
  hierarchyPath: string

  @Prop({ required: true })
  facetCounts: FacetCount

  @Prop()
  isPriceSearch: boolean

  @Prop()
  hierarchyTree: HierarchyTree

  get hierarchyName() {
    logger('hierarchyName', this.searchWizardStore.hierarchy)
    return this.searchWizardStore.hierarchy?.name
  }

  get showCount() {
    return this.searchWizardStore.showCount.value
  }

  async selectHierarchy(node: HierarchyTree) {
    this.$emit('changed', node.path)
    this.$bubble('hierarchy', node.path)
    await this.$nextTick()
    this.scrollToHierarchy()
  }

  shouldShowLocation(path: string) {
    // always show locations above our current path regardless of counts
    if (this.hierarchyPath.startsWith(path)) {
      return true
    }
    // if not showing counts then show all
    if (!this.searchWizardStore.showCount) {
      return true
    }
    // if lower then show if we have a count
    return !!this.facetCounts[path] || path === '/'
  }

  scrollToHierarchy() {
    scrollIntoView('[data-filter-location-scroll-target]', { block: 'start' })
  }

  showCounts(path: string) {
    if (!this.searchWizardStore.showCount) {
      return false
    }
    // show counts below us
    return this.facetCounts[path] ? true : path.startsWith(this.hierarchyPath)
  }
}

export default toNative(FilterAndSortLocation)
</script>

<style scoped>
.disabled {
  opacity: 0.75;
  filter: grayscale(1);
  pointer-events: none;
}

.selected {
  font-weight: bold;
}

.hierarchy :deep(.ui-collapse-content) {
  padding: 1em 0;
}

.hierarchy :deep(.ui-collapse-content .count) {
  margin: 0 2em 0 auto;
}

.hierarchy :deep(.ui-collapse-header .count) {
  font-size: var(--small);
  font-weight: normal;
  color: var(--colour-text-light);
}

.hierarchy :deep(li label) {
  padding: 0.25em 0;
  font-weight: normal;
}

.hierarchy :deep(ul) {
  margin-left: 1.5em;
}

.desktop [data-filter-location-scroll-target] {
  scroll-margin-top: 3.1em;
}
</style>
